import React, { useMemo } from 'react';
import styled from 'styled-components';
import { DropdownItemOrGroup, TDropdownElementProps } from './DropdownItemOrGroup';
import { IPosition, positionStyles } from '../shared';

interface IStyledListProps {
  position: IPosition;
  isInline?: boolean;
  show: boolean;
  zIndex?: number | null;
  topValue?: boolean;
}

const StyledList = styled.ul<IStyledListProps>`
  ${positionStyles};
  display: ${(props) => (props.show ? 'block' : 'none')};
  border-radius: 6px;
  box-shadow:
    0px 0px 0px 1px ${(props) => props.theme.colors.focusHoverShadow},
    0px 10px 15px -3px ${(props) => props.theme.colors.shadowVariant1},
    0px 4px 6px -2px ${(props) => props.theme.colors.focusHoverShadow};
  box-sizing: border-box;
  height: auto;
  list-style-type: none;
  margin: 0;
  padding: 0;
  position: absolute;
  transform: ${(props) => (props.position.x === '50%' ? 'translateX(-50%)' : '')};
  white-space: nowrap;
  z-index: ${(props) => props.zIndex || '1'};
  top: ${(props) => (props.topValue ? '72px' : '46px')};
`;

const StyledListItem = styled.li`
  > * {
    padding: ${(props) => props.theme.spacing.baseValue * 2}px ${(props) => props.theme.spacing.baseValue * 4}px !important;
    border: 0;
  }
  :nth-child(1) > * {
    border-top-left-radius: ${(props) => props.theme.spacing.spacing2};
    border-top-right-radius: ${(props) => props.theme.spacing.spacing2};
  }
  :nth-last-child(1) > * {
    border-bottom-left-radius: ${(props) => props.theme.spacing.spacing2};
    border-bottom-right-radius: ${(props) => props.theme.spacing.spacing2};
  }
`;

export enum IDropdownAnchor {
  topCenter,
  topLeft,
  topRight,
  bottomCenter,
  bottomLeft,
  bottomRight,
}

export interface IDropdownProps {
  position?: IPosition;
  anchor?: IDropdownAnchor;
  // isInline is optional, and usually given by text, or a button that isInline itself so that the dropdown appears a touch higher than normal.
  isInline?: boolean;
  items?: TDropdownElementProps[];
  show: boolean;
  zIndex?: number | null;
  topValue?: boolean;
  children?: React.ReactNode;
}

const genPositionFromAnchor = (anchor: IDropdownAnchor, isInline?: boolean): IPosition => {
  switch (anchor) {
    case IDropdownAnchor.bottomCenter: {
      return {
        anchor: 'top-left',
        x: '50%',
        y: isInline ? '24px' : '40px',
      };
    }
    case IDropdownAnchor.bottomRight: {
      return {
        anchor: 'top-left',
        x: '0%',
        y: isInline ? '24px' : '40px',
      };
    }
    case IDropdownAnchor.bottomLeft: {
      return {
        anchor: 'top-right',
        x: '0%',
        y: isInline ? '24px' : '40px',
      };
    }
    case IDropdownAnchor.topCenter: {
      return {
        anchor: 'bottom-left',
        x: '50%',
        y: isInline ? '24px' : '40px',
      };
    }
    case IDropdownAnchor.topRight: {
      return {
        anchor: 'bottom-left',
        x: '0%',
        y: isInline ? '24px' : '40px',
      };
    }
    case IDropdownAnchor.topLeft: {
      return {
        anchor: 'bottom-right',
        x: '0%',
        y: isInline ? '24px' : '40px',
      };
    }
  }
};

export const Dropdown: React.FC<IDropdownProps> = ({
  children,
  isInline,
  items,
  position,
  anchor = IDropdownAnchor.bottomLeft,
  show,
  zIndex,
  topValue,
}) => {
  const normalizedPosition = useMemo(
    () => position || genPositionFromAnchor(anchor, isInline),
    [position, anchor, isInline]
  );
  return (
    <StyledList isInline={isInline} position={normalizedPosition} show={show} zIndex={zIndex} topValue={topValue}>
      {!!items &&
        items.map((item, index) => (
          <StyledListItem key={index}>
            <DropdownItemOrGroup item={item} />
          </StyledListItem>
        ))}
      {React.Children.map(children, (child, index) => (
        <StyledListItem key={index}>{child}</StyledListItem>
      ))}
    </StyledList>
  );
};
